/**
 * useClassificationPolicy — which classification CONTROLS a Library document
 * or folder form shows for the active organization (Library LB6-T7).
 *
 * Decision 28 (B7): a document's sensitivity and export-control controls are
 * hidden until a compliance framework is active, by the rule for people's
 * clearance fields; decision 37: a folder's follow the same rule. OQ-L6-1:
 * per field — sensitivity under ITAR or CMMC, export control under ITAR only.
 * While a control is hidden it is NEITHER SHOWN NOR SENT: the forms build
 * their request through `classificationFields` / `appendClassification`
 * below, which carry only the visible keys — never a default value for a
 * hidden one (the api answers 400 FRAMEWORK_NOT_ACTIVE if one arrives).
 *
 * Backend: GET /api/v1/knowledge/classification-policy — the one answer the
 * api's writers also ask. The use-identity-field-policy.ts pattern, with one
 * difference that matters: no answer, a failed read (even after a good one)
 * and an answer that does not parse are all the HIDDEN view. A control is
 * shown only on a good answer that says so.
 *
 * A stored classification is still enforced by the api whatever this says:
 * hiding a control never opens a document up.
 *
 * NOT KNOWING IS NOT "HIDDEN" FOR A CREATE (review LB6T7-BF-01). Hidden is the
 * permissive answer for a write: a create that sends no key is stored
 * unclassified / none, and the api cannot tell "hidden" from "the screen did
 * not know". So `ready` is true only on a current good answer, and the
 * upload and folder-create forms do not submit without it — a failed read
 * says so, with a retry, instead of creating an unclassified item in a
 * tenant whose frameworks show the controls.
 */

import { useQuery, type QueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';
import { useAuth } from '@/contexts/auth-context';
import {
  ClassificationControlPolicySchema,
  type ClassificationControlKey,
  type ClassificationControlPolicy,
} from '@argusai/shared/schemas/identity-field-policy';

export const CLASSIFICATION_POLICY_PATH = '/knowledge/classification-policy';

/**
 * The policy's query key: per ORGANIZATION (review LB6T7-BF-06) — a tenant
 * switch or an impersonation never reads the previous tenant's answer.
 */
export function classificationPolicyQueryKey(organizationId: string | null | undefined): readonly unknown[] {
  return ['classification-policy', organizationId ?? null];
}

/**
 * Re-ask the policy, every organization's (review LB6T7-BF-06). A form calls
 * it when a save fails — a framework turned on or off since the form opened
 * answers 400 FRAMEWORK_NOT_ACTIVE (or a write the old answer did not offer),
 * and the form then shows the controls the tenant has now.
 */
export function invalidateClassificationPolicy(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: ['classification-policy'] });
}

/** What a form needs: which of the two controls to render (and send). */
export interface ClassificationControlsView {
  sensitivity: boolean;
  exportControl: boolean;
  /** Either control shown — the form renders its Classification section. */
  any: boolean;
}

/** No answer, an error, an unparseable answer: nothing shown, nothing sent. */
export const HIDDEN_CLASSIFICATION_CONTROLS: ClassificationControlsView = Object.freeze({
  sensitivity: false,
  exportControl: false,
  any: false,
});

/** The view a GOOD policy answer gives. */
export function classificationControlsView(policy: ClassificationControlPolicy): ClassificationControlsView {
  const sensitivity = policy.visible.sensitivity === true;
  const exportControl = policy.visible.exportControl === true;
  return { sensitivity, exportControl, any: sensitivity || exportControl };
}

export interface ClassificationPolicyState {
  controls: ClassificationControlsView;
  /**
   * A CURRENT good answer is in hand: parsed, not failed, not being re-asked.
   * A form that CREATES a document or folder submits only while this is true
   * (review LB6T7-BF-01): without it "hidden" would send no key and the api
   * would store unclassified / none in a tenant whose frameworks show the
   * controls — a fail-open. An edit form need not wait: a key it does not
   * send leaves the stored classification as it is.
   */
  ready: boolean;
  /** The read failed or did not parse — the form says so and offers `retry`. */
  unavailable: boolean;
  isLoading: boolean;
  error: unknown;
  retry: () => void;
}

export function useClassificationPolicy(options: { enabled?: boolean } = {}): ClassificationPolicyState {
  const { user } = useAuth();
  const { data, isLoading, isError, isFetching, error, refetch } = useQuery<unknown>({
    queryKey: classificationPolicyQueryKey(user?.organizationId),
    queryFn: () => apiClient.get<unknown>(CLASSIFICATION_POLICY_PATH),
    // Re-asked whenever a form opens (review LB6T7-BF-06): a framework turned
    // on or off is seen by the next form, not up to a minute later.
    staleTime: 0,
    enabled: options.enabled ?? true,
  });
  const retry = () => void refetch();

  if (isError) return { controls: HIDDEN_CLASSIFICATION_CONTROLS, ready: false, unavailable: true, isLoading, error, retry };
  if (data === undefined) return { controls: HIDDEN_CLASSIFICATION_CONTROLS, ready: false, unavailable: false, isLoading, error, retry };
  const parsed = ClassificationControlPolicySchema.safeParse(data);
  if (!parsed.success) {
    return { controls: HIDDEN_CLASSIFICATION_CONTROLS, ready: false, unavailable: true, isLoading, error: parsed.error, retry };
  }
  return {
    controls: classificationControlsView(parsed.data),
    ready: !isFetching,
    unavailable: false,
    isLoading,
    error: null,
    retry,
  };
}

/**
 * The classification keys a request may carry: ONLY the visible ones, with
 * the form's values. A hidden control contributes no key at all.
 */
export function classificationFields(
  controls: ClassificationControlsView,
  values: Readonly<Record<ClassificationControlKey, string>>,
): Partial<Record<ClassificationControlKey, string>> {
  const out: Partial<Record<ClassificationControlKey, string>> = {};
  if (controls.sensitivity) out.sensitivity = values.sensitivity;
  if (controls.exportControl) out.exportControl = values.exportControl;
  return out;
}

/** `classificationFields`, appended to a multipart upload. */
export function appendClassification(
  formData: FormData,
  controls: ClassificationControlsView,
  values: Readonly<Record<ClassificationControlKey, string>>,
): void {
  for (const [key, value] of Object.entries(classificationFields(controls, values))) {
    formData.append(key, value);
  }
}
